import { useState, useEffect } from "react";
import { database, auth } from "../firebase";
import { ref, onValue, off } from "firebase/database";
import { onAuthStateChanged } from "firebase/auth";

const RateChangeBarcodePrint = () => {
  // State for form inputs
  const [formData, setFormData] = useState({
    type: "Product",
    productName: "",
    barcodesPerPage: '', // Initialize barcodes per page
  });

  // State for products and product selection
  const [currentUser, setCurrentUser] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productNames, setProductNames] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedProductQty, setSelectedProductQty] = useState("");
  const [barcodesPerPage, setBarcodesPerPage] = useState('');
  const [qtyError, setQtyError] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showProductSelection, setShowProductSelection] = useState(false);

  // Encode email for Firebase path
  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, "_");
  };

  // Check for authenticated user
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Fetch products from Firebase
  useEffect(() => {
    if (!currentUser) return;

    const fetchProducts = async () => {
      try {
        setLoading(true);
        const encodedEmail = encodeEmailForPath(currentUser.email);
        const productsRef = ref(database, `users/${encodedEmail}/products`);

        onValue(
          productsRef,
          (snapshot) => {
            const productsData = snapshot.val();
            if (productsData) {
              const productList = Object.keys(productsData).map((productId) => ({
                id: productId,
                ...productsData[productId],
              }));
              setAllProducts(productList);
              setProductNames([...new Set(productList.map((p) => p.productName))]);
              setFilteredProducts([]);
              setSelectedProductId("");
              setSelectedProductQty("");
              setQtyError("");
              setShowProductSelection(false);
              setLoading(false);
            } else {
              setAllProducts([]);
              setProductNames([]);
              setFilteredProducts([]);
              setSelectedProductId("");
              setSelectedProductQty("");
              setQtyError("");
              setShowProductSelection(false);
              setLoading(false);
            }
          },
          (error) => {
            console.error("Error fetching products:", error);
            setErrorMessage(`Error fetching products: ${error.message}`);
            setLoading(false);
          }
        );
      } catch (error) {
        console.error("Firebase fetch error:", error);
        setErrorMessage(`Firebase error: ${error.message}`);
        setLoading(false);
      }
    };

    fetchProducts();

    return () => {
      if (currentUser) {
        const encodedEmail = encodeEmailForPath(currentUser.email);
        const productsRef = ref(database, `users/${encodedEmail}/products`);
        off(productsRef);
      }
    };
  }, [currentUser]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "productName") {
      setFilteredProducts([]);
      setSelectedProductId("");
      setSelectedProductQty("");
      setQtyError("");
      setShowProductSelection(false);
    }
  };

  // Handle product selection from dropdown
  const handleProductSelect = (e) => {
    const productId = e.target.value;
    setSelectedProductId(productId);
    setQtyError("");

    if (productId) {
      const selectedProduct = filteredProducts.find((product) => product.id === productId);
      if (selectedProduct) {
        setSelectedProductQty(selectedProduct.openingStock?.toString() || "0");
        setFilteredProducts((prevProducts) =>
          prevProducts.map((product) =>
            product.id === productId
              ? { ...product, netRate: (Number.parseFloat(product.sellingRate || 0) * selectedProduct.openingStock).toFixed(2) }
              : product
          )
        );
      }
    } else {
      setSelectedProductQty("");
      setFilteredProducts((prevProducts) =>
        prevProducts.map((product) => ({ ...product, netRate: (Number.parseFloat(product.sellingRate || 0)).toFixed(2) }))
      );
    }
  };

  // Handle quantity input change with validation
  const handleQtyChange = (e) => {
    const value = e.target.value;
    setSelectedProductQty(value);

    if (value === "") {
      setQtyError("Quantity is required");
      setFilteredProducts((prevProducts) =>
        prevProducts.map((product) =>
          product.id === selectedProductId
            ? { ...product, netRate: (Number.parseFloat(product.sellingRate || 0)).toFixed(2) }
            : product
        )
      );
      return;
    }

    const qty = Number.parseInt(value);
    if (isNaN(qty) || qty <= 0) {
      setQtyError("Quantity must be a positive number");
      setFilteredProducts((prevProducts) =>
        prevProducts.map((product) =>
          product.id === selectedProductId
            ? { ...product, netRate: (Number.parseFloat(product.sellingRate || 0)).toFixed(2) }
            : product
        )
      );
      return;
    }

    const selectedProduct = filteredProducts.find((p) => p.id === selectedProductId);
    if (selectedProduct && qty > (selectedProduct.openingStock || 0)) {
      setQtyError(`Quantity cannot exceed available stock (${selectedProduct.openingStock || 0})`);
      setFilteredProducts((prevProducts) =>
        prevProducts.map((product) =>
          product.id === selectedProductId
            ? { ...product, netRate: (Number.parseFloat(product.sellingRate || 0)).toFixed(2) }
            : product
        )
      );
      return;
    }

    if (selectedProduct) {
      const unitPrice = Number.parseFloat(selectedProduct.sellingRate || 0);
      const newNetRate = (unitPrice * qty).toFixed(2);
      setFilteredProducts((prevProducts) =>
        prevProducts.map((product) =>
          product.id === selectedProductId ? { ...product, netRate: newNetRate } : product
        )
      );
      setQtyError("");
    }
  };

  // Handle barcodes per page change
  const handleBarcodesPerPageChange = (e) => {
    setBarcodesPerPage(Number.parseInt(e.target.value));
  };

  // Handle Search
  const handleSearch = () => {
    setSuccessMessage("");
    setErrorMessage("");

    if (!formData.productName) {
      setErrorMessage("Please select a product name first");
      return;
    }

    const selectedProducts = allProducts.filter((product) => product.productName === formData.productName);
    if (selectedProducts.length > 0) {
      const mappedProducts = selectedProducts.map((product) => ({
        id: product.id,
        productName: product.productName || "N/A",
        barCode: product.barcode || "N/A",
        barcodeType: product.barcodeType || "N/A",
        newMRP: product.mrp ? Number.parseFloat(product.mrp).toFixed(2) : "0.00",
        discount: product.discount ? Number.parseFloat(product.discount).toFixed(2) : "0",
        openingStock: product.openingStock ? Number.parseInt(product.openingStock) : 0,
        netRate: product.sellingRate ? Number.parseFloat(product.sellingRate).toFixed(2) : "0.00",
        sellingRate: product.sellingRate ? Number.parseFloat(product.sellingRate).toFixed(2) : "0.00",
        labelNo: product.labelNo || "N/A",
        type: product.type || "N/A",
        location: product.location || "N/A",
        designNo: product.designNo || "N/A",
      }));

      setFilteredProducts(mappedProducts);
      setSelectedProductId("");
      setSelectedProductQty("");
      setQtyError("");
      setShowProductSelection(true);
      setSuccessMessage(`Found ${mappedProducts.length} products for ${formData.productName}`);
    } else {
      setFilteredProducts([]);
      setShowProductSelection(false);
      setErrorMessage("No products found for the selected name");
    }
  };

  // Styles matching ProductForm
  const styles = {
    container: {
      maxWidth: "1200px",
      margin: "2rem auto",
      padding: "2rem",
      backgroundColor: "#ffffff",
      borderRadius: "16px",
      boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      minHeight: "100vh",
    },
    formWrapper: {
      backgroundColor: "#ffffff",
      borderRadius: "20px",
      padding: "2.5rem",
      border: "1px solid #e2e8f0",
    },
    heading: {
      fontSize: "2rem",
      fontWeight: "700",
      marginBottom: "2rem",
      color: "#1f2937",
      textAlign: "center",
    },
    messageSuccess: {
      padding: "1rem 1.5rem",
      marginBottom: "1.5rem",
      borderRadius: "12px",
      backgroundColor: "#d1fae5",
      color: "#065f46",
      border: "1px solid #a7f3d0",
      fontSize: "0.9rem",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    messageError: {
      padding: "1rem 1.5rem",
      marginBottom: "1.5rem",
      borderRadius: "12px",
      backgroundColor: "#fee2e2",
      color: "#b91c1c",
      border: "1px solid #fecaca",
      fontSize: "0.9rem",
      fontWeight: "500",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    loadingContainer: {
      textAlign: "center",
      padding: "4rem 2rem",
      color: "#6b7280",
    },
    loadingSpinner: {
      width: "3rem",
      height: "3rem",
      border: "4px solid #e5e7eb",
      borderTop: "4px solid #2563eb",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
      margin: "0 auto 1rem",
    },
    section: {
      backgroundColor: "#fff",
      padding: "1.5rem",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
      marginBottom: "2rem",
    },
    sectionHeading: {
      fontSize: "1.25rem",
      fontWeight: "600",
      marginBottom: "1.5rem",
      color: "#1f2937",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    formGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
      gap: "1.5rem",
      alignItems: "end",
    },
    inputGroup: {
      display: "flex",
      flexDirection: "column",
      gap: "0.5rem",
    },
    label: {
      display: "block",
      fontSize: "0.875rem",
      fontWeight: "500",
      color: "#374151",
      marginBottom: "0.25rem",
    },
    requiredAsterisk: {
      color: "#ef4444",
      marginLeft: "0.25rem",
    },
    input: {
      display: "block",
      width: "100%",
      padding: "0.75rem",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "0.875rem",
      backgroundColor: "#fff",
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      boxSizing: "border-box",
    },
    inputError: {
      display: "block",
      width: "100%",
      padding: "0.75rem",
      border: "1px solid #ef4444",
      borderRadius: "6px",
      fontSize: "0.875rem",
      backgroundColor: "#fef2f2",
      transition: "border-color 0.2s ease, box-shadow 0.2s ease",
      boxSizing: "border-box",
    },
    inputReadonly: {
      display: "block",
      width: "100%",
      padding: "0.75rem",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "0.875rem",
      backgroundColor: "#f9fafb",
      color: "#6b7280",
      boxSizing: "border-box",
    },
    select: {
      display: "block",
      width: "100%",
      padding: "0.75rem",
      border: "1px solid #d1d5db",
      borderRadius: "6px",
      fontSize: "0.875rem",
      backgroundColor: "#fff",
      cursor: "pointer",
      appearance: "none",
      backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3e%3c/svg%3e")`,
      backgroundPosition: "right 0.75rem center",
      backgroundRepeat: "no-repeat",
      backgroundSize: "1em 1em",
      paddingRight: "2.5rem",
      boxSizing: "border-box",
    },
    buttonContainer: {
      display: "flex",
      gap: "1rem",
      flexWrap: "wrap",
      alignItems: "center",
    },
    button: {
      padding: "0.75rem 1.5rem",
      borderRadius: "6px",
      fontWeight: "500",
      fontSize: "0.875rem",
      cursor: "pointer",
      border: "none",
      transition: "all 0.2s ease",
      backgroundColor: "#2563eb",
      color: "white",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    buttonSecondary: {
      padding: "0.75rem 1.5rem",
      borderRadius: "6px",
      fontWeight: "500",
      fontSize: "0.875rem",
      cursor: "pointer",
      border: "1px solid #d1d5db",
      transition: "all 0.2s ease",
      backgroundColor: "#ffffff",
      color: "#374151",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    buttonSuccess: {
      padding: "0.75rem 1.5rem",
      borderRadius: "6px",
      fontWeight: "500",
      fontSize: "0.875rem",
      cursor: "pointer",
      border: "none",
      transition: "all 0.2s ease",
      backgroundColor: "#10b981",
      color: "white",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    buttonDisabled: {
      padding: "0.75rem 1.5rem",
      borderRadius: "6px",
      fontWeight: "500",
      fontSize: "0.875rem",
      border: "none",
      backgroundColor: "#9ca3af",
      color: "#ffffff",
      cursor: "not-allowed",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    tableContainer: {
      overflowX: "auto",
      borderRadius: "8px",
      border: "1px solid #e2e8f0",
      backgroundColor: "#ffffff",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
    },
    th: {
      backgroundColor: "#f8fafc",
      padding: "1rem 0.75rem",
      textAlign: "left",
      border: "1px solid #e2e8f0",
      fontWeight: "600",
      fontSize: "0.875rem",
      color: "#374151",
    },
    td: {
      padding: "0.75rem",
      border: "1px solid #e2e8f0",
      fontSize: "0.875rem",
      color: "#4b5563",
    },
    totalRow: {
      backgroundColor: "#f1f5f9",
      fontWeight: "600",
      color: "#1f2937",
    },
    errorText: {
      fontSize: "0.75rem",
      color: "#ef4444",
      marginTop: "0.25rem",
    },
    emptyState: {
      textAlign: "center",
      padding: "3rem 2rem",
      color: "#6b7280",
      fontSize: "1rem",
    },
  };

  // Handle Barcode Print
  const handleMultipleBarcodePrint = () => {
    if (!selectedProductId) {
      setErrorMessage("Please select a product first");
      return;
    }

    const selectedProduct = filteredProducts.find((p) => p.id.toString() === selectedProductId.toString());
    if (!selectedProduct) {
      setErrorMessage("Selected product not found. Please try selecting the product again.");
      return;
    }

    const layout = getLayoutConfig(barcodesPerPage);
    const barcodeArray = Array(barcodesPerPage).fill(selectedProduct);

    // Create the HTML content for multiple barcodes with fold layout
    const htmlContent = `
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Multiple Barcodes - ${selectedProduct.productName} (${barcodesPerPage} per page)</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.5/dist/JsBarcode.all.min.js"></script>
        <style>
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background-color: #ffffff;
          }
          .page-container {
            width: ${layout.pageWidth}px;
            min-height: ${layout.pageHeight}px;
            margin: 0 auto;
            display: grid;
            grid-template-columns: repeat(${layout.cols}, 1fr);
            grid-template-rows: repeat(${layout.rows}, 1fr);
            gap: 10px;
            padding: 20px;
            page-break-after: always;
          }
          .barcode-container {
            width: ${layout.width}px;
            height: ${layout.height}px;
            text-align: center;
            padding: 5px;
            border: 1px dashed #ccc;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            box-sizing: border-box;
          }
          .barcode-header {
            flex: 0 0 auto;
          }
          .barcode-header h2 {
            font-size: 10px;
            font-weight: bold;
            margin: 0;
            margin-bottom: 2px;
            color: #000;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .barcode-mrp {
            font-size: 9px;
            text-align: center;
            margin-bottom: 3px;
            color: #000;
          }
          .barcode-container canvas {
            margin: 0 auto;
            max-width: 100%;
            flex: 0 0 auto;
          }
          .barcode-number {
            font-size: 8px;
            margin-top: 2px;
            color: #000;
            flex: 0 0 auto;
          }
          .barcode-fold {
            font-size: 8px;
            color: #000;
            margin-top: 2px;
            flex: 0 0 auto;
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 5px;
          }
          .barcode-fold span {
            background-color: #f0f0f0;
            padding: 2px 4px;
            border-radius: 3px;
            white-space: nowrap;
          }
          .controls {
            text-align: center;
            margin-bottom: 20px;
            padding: 10px;
            background-color: #f5f5f5;
            border-radius: 5px;
          }
          .print-button, .download-button {
            padding: 8px 16px;
            background-color: #3182CE;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 12px;
            font-weight: 600;
            margin: 0 5px;
            transition: background-color 0.2s;
          }
          .print-button:hover, .download-button:hover {
            background-color: #2C5282;
          }
          @media print {
            .controls {
              display: none;
            }
            body {
              padding: 0;
            }
            .page-container {
              margin: 0;
              padding: 10px;
            }
            .barcode-container {
              border: none;
            }
          }
        </style>
      </head>
      <body>
        <div class="controls">
          <h3>Barcode Print Sheet - ${selectedProduct.productName} (${barcodesPerPage} per page)</h3>
          <button class="print-button" onclick="window.print()">🖨️ Print</button>
          <button class="download-button" onclick="downloadAllBarcodes()">📥 Download</button>
        </div>
        
        <div class="page-container">
          ${barcodeArray.map((product, index) => `
            <div class="barcode-container">
              <div class="barcode-header">
                <h2>${product.productName}</h2>
                <div class="barcode-mrp">MRP: ₹${product.newMRP}</div>
              </div>
              <canvas id="barcode-${index}"></canvas>
              <div class="barcode-number">${product.barCode}</div>
              <div class="barcode-fold">
                ${product.labelNo !== "N/A" ? `<span>Label No: ${product.labelNo}</span>` : ''}
                ${product.type !== "N/A" ? `<span>Type: ${product.type}</span>` : ''}
                ${product.location !== "N/A" ? `<span>Location: ${product.location}</span>` : ''}
                ${product.designNo !== "N/A" ? `<span>Design No: ${product.designNo}</span>` : ''}
              </div>
            </div>
          `).join('')}
        </div>
        
        <script>
          document.addEventListener("DOMContentLoaded", function() {
            ${barcodeArray.map((product, index) => `
              JsBarcode("#barcode-${index}", "${product.barCode}", {
                format: "${product.barcodeType === 'Company Barcode' ? 'CODE128' : 'CODE39'}",
                width: 1,
                height: 30,
                displayValue: false,
                margin: 0
              });
            `).join('')}
          });
          
          function downloadAllBarcodes() {
            const combinedCanvas = document.createElement('canvas');
            const ctx = combinedCanvas.getContext('2d');
            
            combinedCanvas.width = ${layout.pageWidth};
            combinedCanvas.height = ${layout.pageHeight};
            
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, combinedCanvas.width, combinedCanvas.height);
            
            const itemWidth = ${layout.width};
            const itemHeight = ${layout.height};
            const cols = ${layout.cols};
            const gap = 0;
            const padding = 20;
            
            ${barcodeArray.map((product, index) => `
              const row${index} = Math.floor(${index} / cols);
              const col${index} = ${index} % cols;
              const x${index} = padding + col${index} * (itemWidth + gap);
              const y${index} = padding + row${index} * (itemHeight + gap);
              
              ctx.strokeStyle = '#cccccc';
              ctx.setLineDash([5, 5]);
              ctx.strokeRect(x${index}, y${index}, itemWidth, itemHeight);
              ctx.setLineDash([]);
              
              ctx.fillStyle = '#000000';
              ctx.textAlign = 'center';
              
              ctx.font = 'bold 10px Arial';
              ctx.fillText('${product.productName.replace(/'/g, "\\'")}', x${index} + itemWidth/2, y${index} + 15);
              
              ctx.font = '9px Arial';
              ctx.fillText('MRP: ₹${product.newMRP}', x${index} + itemWidth/2, y${index} + 30);
              
              const barcodeCanvas${index} = document.getElementById("barcode-${index}");
              if (barcodeCanvas${index}) {
                const barcodeX${index} = x${index} + (itemWidth - barcodeCanvas${index}.width) / 2;
                ctx.drawImage(barcodeCanvas${index}, barcodeX${index}, y${index} + 35);
              }
              
              ctx.font = '8px Arial';
              ctx.fillText('${product.barCode}', x${index} + itemWidth/2, y${index} + itemHeight - 30);
              
              // Draw fold layout
              ctx.font = '8px Arial';
              let foldY = y${index} + itemHeight - 15;
              const foldItems = [
                ${product.labelNo !== "N/A" ? `'Label No: ${product.labelNo.replace(/'/g, "\\'")}'` : ''},
                ${product.type !== "N/A" ? `'Type: ${product.type.replace(/'/g, "\\'")}'` : ''},
                ${product.location !== "N/A" ? `'Location: ${product.location.replace(/'/g, "\\'")}'` : ''},
                ${product.designNo !== "N/A" ? `'Design No: ${product.designNo.replace(/'/g, "\\'")}'` : ''}
              ].filter(item => item);
              foldItems.forEach((item, i) => {
                if (i % 2 === 0) {
                  ctx.fillText(item, x${index} + itemWidth/4, foldY);
                } else {
                  ctx.fillText(item, x${index} + 3*itemWidth/4, foldY);
                  foldY += 10;
                }
              });
            `).join('')}
            
            const link = document.createElement("a");
            link.href = combinedCanvas.toDataURL("image/png");
            link.download = "barcodes-${selectedProduct.barCode}-${barcodesPerPage}per-page.png";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        </script>
      </body>
      </html>
    `;

    const newWindow = window.open("", "_blank");
    if (newWindow) {
      newWindow.document.open();
      newWindow.document.write(htmlContent);
      newWindow.document.close();

      // Wait for the new window to load, then render barcodes
      newWindow.onload = function () {
        barcodeArray.forEach((product, index) => {
          if (newWindow.document.getElementById(`barcode-${index}`)) {
            // Use CODE128 for 'Company Barcode', otherwise CODE39
            const format = product.barcodeType === 'Company Barcode' ? 'CODE128' : 'CODE39';
            // JsBarcode is loaded in the new window, so use its global
            newWindow.JsBarcode(`#barcode-${index}`, product.barCode, {
              format: format,
              width: 1,
              height: 30,
              displayValue: false,
              margin: 0
            });
          }
        });
      };
    } else {
      setErrorMessage("Popup blocked! Please allow popups for this site.");
    }

    setSuccessMessage(`Generated ${barcodesPerPage} barcodes per page for ${selectedProduct.productName}`);
  };

  // Calculate totals
  const totalQty = filteredProducts.reduce((sum, product) => {
    const qty = product.id === selectedProductId && selectedProductQty
      ? Number.parseInt(selectedProductQty) || 0
      : Number.parseInt(product.openingStock) || 0;
    return sum + qty;
  }, 0);

  const totalNetRate = filteredProducts.reduce((sum, p) => sum + (Number.parseFloat(p.netRate) || 0), 0).toFixed(2);

  // Layout configuration for different barcode counts per page
  const getLayoutConfig = (count) => {
    const configs = {
      12: { rows: 3, cols: 4, width: 200, height: 140, pageWidth: 900, pageHeight: 500 },
      16: { rows: 4, cols: 4, width: 180, height: 120, pageWidth: 800, pageHeight: 600 },
      20: { rows: 5, cols: 4, width: 160, height: 110, pageWidth: 800, pageHeight: 650 },
      24: { rows: 6, cols: 4, width: 150, height: 100, pageWidth: 800, pageHeight: 720 },
    };
    return configs[count] || configs[16]; // Default to 16 if count not found
  };

  // JSX for the component
  return (
    <div style={styles.container}>
      <div style={styles.formWrapper}>
        <h1 style={styles.heading}>🏷️ Product Barcode Print</h1>

        {successMessage && (
          <div style={styles.messageSuccess}>
            <span>✅</span>
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div style={styles.messageError}>
            <span>❌</span>
            {errorMessage}
          </div>
        )}

        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.loadingSpinner}></div>
            <p>Loading product data...</p>
          </div>
        ) : (
          <>
            {/* Product Selection Section */}
            <div style={styles.section}>
              <h2 style={styles.sectionHeading}>📋 Product Selection</h2>
              <div style={styles.formGrid}>
                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    Select Type<span style={styles.requiredAsterisk}>*</span>
                  </label>
                  <select name="type" value={formData.type} onChange={handleInputChange} style={styles.select}>
                    <option value="Product">Product</option>
                  </select>
                </div>

                <div style={styles.inputGroup}>
                  <label style={styles.label}>
                    Product Name<span style={styles.requiredAsterisk}>*</span>
                  </label>
                  <select name="productName" value={formData.productName} onChange={handleInputChange} style={styles.select}>
                    <option value="">Select Product Name</option>
                    {productNames.map((name, index) => (
                      <option key={index} value={name}>
                        {name}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={styles.buttonContainer}>
                  <button
                    onClick={handleSearch}
                    style={styles.button}
                    onMouseOver={(e) => (e.target.style.backgroundColor = "#1d4ed8")}
                    onMouseOut={(e) => (e.target.style.backgroundColor = "#2563eb")}
                  >
                    🔍 Search
                  </button>
                </div>
              </div>
            </div>

            {/* Product Selection & Barcode Options */}
            {showProductSelection && (
              <div style={styles.section}>
                <h2 style={styles.sectionHeading}>📦 Product Selection & Barcode Options</h2>
                <div style={styles.formGrid}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Product Name</label>
                    <select value={selectedProductId} onChange={handleProductSelect} style={styles.select}>
                      <option value="">Select Product</option>
                      {filteredProducts.map((product) => (
                        <option key={product.id} value={product.id}>
                          {product.productName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Bar Code</label>
                    <input
                      type="text"
                      value={selectedProductId ? filteredProducts.find((p) => p.id === selectedProductId)?.barCode || "" : ""}
                      readOnly
                      style={styles.inputReadonly}
                      placeholder="Select a product to view barcode"
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Opening Stock<span style={styles.requiredAsterisk}>*</span></label>
                    <input
                      type="text"
                      value={selectedProductId ? filteredProducts.find((p) => p.id === selectedProductId)?.openingStock || "" : ""}
                      readOnly
                      style={styles.inputReadonly}
                      placeholder="Select a product to view stock"
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Barcodes Per Page</label>
                    <select value={barcodesPerPage} onChange={handleBarcodesPerPageChange} style={styles.select}>
                      <option value="">Select Barcode Per Page</option>
                      <option value={12}>12 Per Page</option>
                      <option value={16}>16 Per Page</option>
                      <option value={20}>20 Per Page</option>
                      <option value={24}>24 Per Page</option>
                    </select>
                  </div>

                  <div style={styles.buttonContainer}>
                    <button
                      onClick={handleMultipleBarcodePrint}
                      style={!selectedProductId ? styles.buttonDisabled : styles.buttonSuccess}
                      disabled={!selectedProductId}
                      onMouseOver={(e) => {
                        if (!e.target.disabled) {
                          e.target.style.backgroundColor = "#059669";
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!e.target.disabled) {
                          e.target.style.backgroundColor = "#10b981";
                        }
                      }}
                    >
                      🏷️ Print {barcodesPerPage} Barcodes
                    </button>
                  </div>
                </div>
                {qtyError && <div style={styles.errorText}>{qtyError}</div>}
              </div>
            )}

            {/* Products Table Section */}
            {filteredProducts.length > 0 && (
              <div style={styles.section}>
                <h2 style={styles.sectionHeading}>📊 Products Overview</h2>
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Product Name</th>
                        <th style={styles.th}>Bar Code</th>
                        <th style={styles.th}>Barcode Type</th>
                        <th style={styles.th}>MRP</th>
                        <th style={styles.th}>Disc(%)</th>
                        <th style={styles.th}>Opening Stock</th>
                      
                        <th style={styles.th}>Label No</th>
                        <th style={styles.th}>Type</th>
                        <th style={styles.th}>Location</th>
                        <th style={styles.th}>Design No</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.map((product) => (
                        <tr key={product.id}>
                          <td style={styles.td}>{product.productName}</td>
                          <td style={styles.td}>{product.barCode}</td>
                          <td style={styles.td}>{product.barcodeType}</td>
                          <td style={styles.td}>₹{product.newMRP}</td>
                          <td style={styles.td}>{product.discount}%</td>
                          <td style={styles.td}>{product.id === selectedProductId && selectedProductQty ? selectedProductQty : product.openingStock}</td>
                 
                          <td style={styles.td}>{product.labelNo}</td>
                          <td style={styles.td}>{product.type}</td>
                          <td style={styles.td}>{product.location}</td>
                          <td style={styles.td}>{product.designNo}</td>
                        </tr>
                      ))}
                      <tr style={styles.totalRow}>
                        <td style={styles.td} colSpan="5">
                          <strong>Total:</strong>
                        </td>
                        <td style={styles.td}>
                          <strong>{totalQty}</strong>
                        </td>
                        
                        <td style={styles.td} colSpan="4"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .formGrid {
            grid-template-columns: 1fr;
          }
          .buttonContainer {
            flex-direction: column;
            align-items: stretch;
          }
          .button, .buttonSecondary, .buttonSuccess, .buttonDisabled {
            width: 100%;
            justify-content: center;
          }
        }
        
        input:focus, select:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
        
        input:hover:not(:disabled), select:hover:not(:disabled) {
          border-color: #9ca3af;
        }
        
        button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        .table tr:hover {
          background-color: #f8fafc;
        }
      `}</style>
    </div>
  );
};

export default RateChangeBarcodePrint;